import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SearchIcon from '@mui/icons-material/Search';
import StoreIcon from '@mui/icons-material/Store';
import { Button, Divider } from '@mui/material';
import { Grid } from '@mui/material';
import { Link } from 'react-router-dom';
import { teceoOrange } from '../../Theme';
import TeamSelectModalWrapper, {
  ListItemButton,
} from '../../components/Modal/TeamSelectModalWrapper';
import Typography from '../../components/Typography/Typography';
import { FeatureFlagEnum } from '../../featureflag/feature-flag.enum';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import LangEnum from '../../lang/LangEnum';
import useTeceoStyles from '../../useTeceoStyles';
import { BrandSelectModalTestIds } from '../../utils/testIds/SelectModals.testIds';
import { useApplicationContext } from '../context/ApplicationContext/ApplicationContext';
import { Brand } from '../context/ApplicationContext/applicationContext.types';
import { useTranslation } from '../translation/TranslationContext';
import useBrandSelectModal from './hooks/useBrandSelectModal';
import useBrandSelectModalStyles from './styles/brandSelectModal.styles';

type BrandSelectModalProps = {
  brands: Array<Brand>;
  open: boolean;
  onClose: () => void;
  onClick?: (brand: Brand) => void;
  isLoading?: boolean;
};

const BrandSelectModal = ({
  brands,
  open,
  onClose,
  onClick,
  isLoading = false,
}: BrandSelectModalProps) => {
  const { classes } = useBrandSelectModalStyles();
  const { classes: teceoClasses } = useTeceoStyles();
  const { translate } = useTranslation();
  const { state } = useApplicationContext();
  const { getFeatureFlag } = useFeatureFlag();
  const { searchBrandsUrl, handleClick, handleGoToStore, handleClickGoToLink } =
    useBrandSelectModal({
      onClose,
      onClick,
    });

  return (
    <TeamSelectModalWrapper
      open={open}
      onClose={onClose}
      headerMessage={
        brands?.length > 0
          ? translate(LangEnum.TEAM_MODAL_CHOOSE_BRAND_TEXT)
          : translate(LangEnum.TEAM_MODAL_NO_CONNECTIONS_YET_TEXT)
      }
      title={translate(LangEnum.TEAM_MODAL_CHOOSE_BRAND_TITLE)}
      isLoading={isLoading}
    >
      {brands?.length > 0 ? (
        <Grid container spacing={2} test-id={BrandSelectModalTestIds.BRAND_LIST_CONTAINER}>
          {brands.map((brand) => (
            <ListItemButton
              key={brand.id}
              avatarContent={brand.name[0]}
              avatarSrc={brand.logo}
              handleClick={() => handleClick(brand)}
              name={brand.name}
              testId={`${BrandSelectModalTestIds.BRAND_LIST_ITEM}-${brand.id}`}
              typographyProps={{
                className: state.brand?.id === brand.id ? teceoClasses.boldText : '',
                color: 'textPrimary',
              }}
            />
          ))}
          {getFeatureFlag(FeatureFlagEnum.ENABLE_SEARCH_BRANDS, true) && (
            <ListItemButton
              avatarContent={<SearchIcon fontSize="small" />}
              name={translate(LangEnum.CONNECTIONS_FIND_BRANDS)}
              style={{ backgroundColor: teceoOrange }}
              testId={BrandSelectModalTestIds.BRAND_SEARCH_BUTTON}
              typographyProps={{ color: 'textPrimary' }}
              handleClick={() => handleClickGoToLink(searchBrandsUrl)}
            />
          )}

          {state.brand && (
            <Grid size={12}>
              <Divider />

              <Button fullWidth onClick={handleGoToStore} className={classes.goToStoreButton}>
                <StoreIcon className={classes.goToStoreIcon} />
                <Typography align="left" color="textSecondary">
                  {translate(LangEnum.BRAND_GO_TO_STORE_BUTTON)}
                </Typography>
              </Button>
            </Grid>
          )}
        </Grid>
      ) : (
        <Grid
          container
          sx={{
            justifyContent: 'center',
          }}
          size={12}
        >
          <Link to={searchBrandsUrl} className={teceoClasses.link}>
            <Button
              variant="contained"
              color="primary"
              endIcon={<ArrowForwardIcon />}
              size="medium"
              onClick={onClose}
            >
              {translate(LangEnum.MAIN_DRAWER_CONNECTIONS)}
            </Button>
          </Link>
        </Grid>
      )}
    </TeamSelectModalWrapper>
  );
};

export default BrandSelectModal;
